import { useCallback, useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { ArrowLeft, ChevronRight, Download, Printer } from 'lucide-react';
import api from '../lib/api';
import Button from '../ui/Button';
import EmptyState from '../ui/EmptyState';
import ErrorState from '../ui/ErrorState';
import Icon from '../ui/Icon';
import ListRow from '../ui/ListRow';
import PageHeader from '../ui/PageHeader';
import Skeleton from '../ui/Skeleton';
import { useToast } from '../ui/Toast';
import ScriptReadView from '../components/ScriptReadView';
import { clean } from '../shell/copy';

// /talent/scripts (HD-9): the performer's scripts on the Portal profile (DESIGN rev 2.1 §3.3). The list is List rows
// (script, client); a row opens the script to read (?script=<id>, so the home page's shoot rows can link straight to
// one) in the shared ScriptReadView. Same calls: GET /api/talent-portal/scripts (bodies included), and the series
// screenplay GET /api/talent-portal/scripts/:id/pdf (opens the signed URL; a failure is a toast). Print prints the open
// script with the browser (the top bar and the actions do not print).
export default function TalentScriptView() {
  const navigate = useNavigate();
  const toast = useToast();
  const [params, setParams] = useSearchParams();
  const [scripts, setScripts] = useState([]);
  const [state, setState] = useState('loading');   // loading | data | failed
  const openId = params.get('script');

  const load = useCallback(() => {
    setState('loading');
    api.get('/api/talent-portal/scripts')
      .then(r => { setScripts(r.data); setState('data'); })
      .catch(() => setState('failed'));
  }, []);
  useEffect(() => { load(); }, [load]);

  const open = id => setParams(id ? { script: id } : {});
  const openScript = openId ? scripts.find(s => s.id === openId) : null;

  async function openPdf(s) {
    try {
      const r = await api.get(`/api/talent-portal/scripts/${s.id}/pdf`);
      window.open(r.data.url, '_blank', 'noopener');
    } catch {
      toast('The screenplay PDF did not open. Try again.', { tone: 'danger' });
    }
  }

  if (openId && state === 'data') {
    if (!openScript) {
      return (
        <main className="ui ui-page ui-page--portal">
          <PageHeader title="Script not found" actions={<Button variant="quiet" portal icon={ArrowLeft} onClick={() => open(null)}>All your scripts</Button>} />
          <p className="ui-p ui-text-2">This script is not on your list. It may belong to a shoot you are no longer booked on. Open your scripts to see the ones you have.</p>
        </main>
      );
    }
    return (
      <main className="ui ui-page ui-page--portal ui-page--read">
        <PageHeader title={clean(openScript.scriptName)} meta={clean(openScript.clientName)}
          actions={(
            <>
              <Button variant="quiet" portal icon={ArrowLeft} onClick={() => open(null)}>All your scripts</Button>
              {openScript.sourcePdfName && (
                <Button variant="secondary" portal icon={Download} data-testid="talent-series-pdf" onClick={() => openPdf(openScript)}
                  title={`Download the series screenplay, ${clean(openScript.sourcePdfName)}`}>Screenplay PDF</Button>
              )}
              <Button variant="secondary" portal icon={Printer} onClick={() => window.print()}>Print</Button>
            </>
          )} />
        <ScriptReadView body={openScript.scriptBody} />
      </main>
    );
  }

  return (
    <main className="ui ui-page ui-page--portal">
      <PageHeader title="Your scripts" meta="Approved scripts for the shoots you are booked on"
        actions={<Button variant="quiet" portal icon={ArrowLeft} onClick={() => navigate('/talent')}>Your next shoots</Button>} />
      <section className="ui-section" aria-label="Your scripts">
        {state === 'loading' && (
          <div className="ui-region ui-list" aria-busy="true">
            {[0, 1, 2].map(i => <ListRow key={i} portal title={<Skeleton width="45%" label={i === 0 ? 'Loading your scripts' : undefined} />} meta={<Skeleton width="20%" />} />)}
          </div>
        )}
        {state === 'failed' && <ErrorState title="Your scripts did not load" message="Check your connection and try again." onRetry={load} />}
        {state === 'data' && scripts.length === 0 && (
          <EmptyState message="No scripts yet. A script shows here once it is approved for a shoot you are booked on." />
        )}
        {state === 'data' && scripts.length > 0 && (
          <div className="ui-region ui-list">
            {scripts.map(s => (
              <ListRow key={s.id} portal title={clean(s.scriptName)} meta={clean(s.clientName)} data-key={s.id}
                trailing={<Icon icon={ChevronRight} size={20} />} onClick={() => open(s.id)} />
            ))}
          </div>
        )}
      </section>
    </main>
  );
}
